import React, { useState, useEffect, useCallback } from 'react';
import { Star, CheckCircle, XCircle, Trash2, RefreshCw, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchReviews = useCallback(async () => {
    try {
      const response = await api.get('/admin/reviews');
      setReviews(response.data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleApprove = async (id) => {
    await api.put(`/admin/reviews/${id}/approve`);
    fetchReviews();
  };

  const handleReject = async (id) => {
    await api.put(`/admin/reviews/${id}/reject`);
    fetchReviews();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    await api.delete(`/admin/reviews/${id}`);
    fetchReviews();
  };

  const filteredReviews = filter === 'all' ? reviews : reviews.filter(r => r.status === filter);

  const statusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="text-xs bg-[#00FF88]/20 text-[#00FF88] px-2 py-0.5 rounded-full">Approuv&eacute;</span>;
      case 'rejected': return <span className="text-xs bg-[#FF3B5C]/20 text-[#FF3B5C] px-2 py-0.5 rounded-full">Rejet&eacute;</span>;
      default: return <span className="text-xs bg-[#FFB800]/20 text-[#FFB800] px-2 py-0.5 rounded-full">En attente</span>;
    }
  };

  return (
    <div className="p-8" data-testid="reviews-management">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Avis Clients</h1>
        <p className="text-[#B8B8BA]">G&eacute;rez les avis laiss&eacute;s par vos clients</p>
      </motion.div>

      {/* Filters */}
      <div className="flex items-center space-x-3 mb-6">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'pending', label: 'En attente' },
          { key: 'approved', label: `Approuv\u00e9s` },
          { key: 'rejected', label: `Rejet\u00e9s` }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key ? 'bg-[#FF6B35] text-white' : 'bg-[#222226] text-[#B8B8BA] hover:text-white border border-[#3A3A3D]'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label} {f.key !== 'all' && `(${reviews.filter(r => r.status === f.key).length})`}
          </button>
        ))}
        <button onClick={fetchReviews} className="ml-auto text-[#00FFFF] hover:text-white p-2" data-testid="refresh-reviews">
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6B6B6E]">Chargement...</div>
      ) : filteredReviews.length === 0 ? (
        <div className="text-center py-12 text-[#6B6B6E]">Aucun avis</div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
              data-testid={`review-item-${review.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-white font-bold">{review.user_name}</span>
                    {statusBadge(review.status)}
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#3A3A3D]'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-[#E8E8E8] text-sm mb-2">{review.comment}</p>
                  <p className="text-[#6B6B6E] text-xs flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>{review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                  </p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {review.status !== 'approved' && (
                    <button onClick={() => handleApprove(review.id)} className="p-2 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg hover:bg-[#00FF88]/20 transition-colors" title="Approuver" data-testid={`approve-${review.id}`}>
                      <CheckCircle className="h-4 w-4 text-[#00FF88]" />
                    </button>
                  )}
                  {review.status !== 'rejected' && (
                    <button onClick={() => handleReject(review.id)} className="p-2 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg hover:bg-[#FFB800]/20 transition-colors" title="Rejeter" data-testid={`reject-${review.id}`}>
                      <XCircle className="h-4 w-4 text-[#FFB800]" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(review.id)} className="p-2 bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 rounded-lg hover:bg-[#FF3B5C]/20 transition-colors" title="Supprimer" data-testid={`delete-${review.id}`}>
                    <Trash2 className="h-4 w-4 text-[#FF3B5C]" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
